/**
 * src/features/admin/ui/FunnelRow.tsx
 *
 * Conversion funnel mini-cards: signup, activation, paid (ADMIN-01).
 * Hidden when all three values are 0.
 * Extracted from MetricsGrid to keep component size under 200 lines (CLAUDE.md rule 5).
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/shared/ui/card';
import type { AdminMetrics } from '@/shared/types/admin';

export function FunnelRow({ funnel }: { funnel: AdminMetrics['funnel'] }): React.ReactElement | null {
  const { t, i18n } = useTranslation();

  const allZero = funnel.signup === 0 && funnel.activation === 0 && funnel.paid === 0;
  if (allZero) return null;

  const funnelCards: Array<{ key: string; label: string; value: number; accent?: boolean }> = [
    {
      key: 'signup',
      label: t('admin.metric.funnel_signup', 'Signups'),
      value: funnel.signup,
    },
    {
      key: 'activation',
      label: t('admin.metric.funnel_activation', 'Activations'),
      value: funnel.activation,
    },
    {
      key: 'paid',
      label: t('admin.metric.funnel_paid', 'Paid'),
      value: funnel.paid,
      accent: true,
    },
  ];

  return (
    <>
      <p className="text-xs font-bold uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-4">
        {t('admin.section.funnel', 'Conversion Funnel')}
      </p>
      <div className="grid grid-cols-3 gap-3 md:gap-4">
        {funnelCards.map(({ key, label, value, accent }) => (
          <Card key={key}>
            <CardContent className="p-6 flex flex-col gap-2">
              <dt className="text-caption uppercase tracking-wide text-slate-500 dark:text-slate-400 font-semibold">
                {label}
              </dt>
              <dd
                className={`text-subhead font-bold font-mono tabular-nums ${
                  accent
                    ? 'text-blue-600 dark:text-blue-400'
                    : 'text-slate-900 dark:text-slate-50'
                }`}
              >
                {value.toLocaleString(i18n.language)}
              </dd>
            </CardContent>
          </Card>
        ))}
      </div>
    </>
  );
}
